import classNames from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerEnterFullScreen,
  requestVideoPlayerLeaveFullScreen,
  requestVideoPlayerMute,
  requestVideoPlayerPause,
  requestVideoPlayerPlay,
  requestVideoPlayerPlaybackRate,
  requestVideoPlayerUnmute,
  requestVideoPlayerVolume,
  updateTheaterMode,
} from 'actions/videoPlayer';
import { NERVOUS } from 'constants/emojiReactions';
import { FULL_SCREEN_CLICK, THEATER_MODE_TOGGLED } from 'constants/events';
import * as analytics from 'utilities/analytics';
import { isElectron } from 'utilities/device';

import { selectIsActive } from 'selectors/viewerInsights';

import FullscreenButton from './fullscreen-button';
import PipButton from './pip-button';
import PlayPauseButton from './play-pause-button';
import PlaybackRateControl from './playback-rate-control';
import SeekBar from './seek-bar';
import StepBackFiveButton from './step-back-five-button';
import StepForwardFiveButton from './step-forward-five-button';
import TheaterModeButton from './theaterModeButton';
import TimeDisplay from './time-display';
import VolumeControl from './volume-control';
import EmojiBar from '../emoji-bar';
import { KEY_THEATER_MODE } from '../../../../../shared/constants/cookie';

import './styles.less';

class ControlBar extends Component {
  render() {
    const {
      controlsActive,
      currentTime,
      currentUserIsOwner,
      duration,
      ended,
      fullScreen,
      inTrimMode,
      isMainVideoOnSharePage,
      muted,
      paused,
      playbackRate,
      seeking,
      theaterMode,
      useEmojis,
      volume,
      volumeControlActive,
      isViewerInsightsActive,
      pipSupported,
      togglePictureInPicture,
    } = this.props;

    const controlBarExpanded =
      controlsActive || inTrimMode || isViewerInsightsActive;
    const showEmojiBarInsidePlayer =
      (!inTrimMode && useEmojis && isMainVideoOnSharePage && fullScreen) ||
      (!isMainVideoOnSharePage && !ended && useEmojis);

    let fullScreenButton = null;
    let playbackRateControl = null;

    const showPipButton = pipSupported && !isElectron;

    if (!inTrimMode) {
      fullScreenButton = (
        <FullscreenButton
          fullScreen={fullScreen}
          seeking={seeking}
          onClick={this.handleToggleFullscreen}
        />
      );

      playbackRateControl = (
        <PlaybackRateControl
          playbackRate={playbackRate}
          seeking={seeking}
          onRateChange={this.props.requestVideoPlayerPlaybackRate}
        />
      );
    }

    return (
      <div className="video-player-control-bar-container">
        <div
          className={classNames(
            'control-bar',
            controlBarExpanded && 'expanded'
          )}
          ref={controlBar => (this.controlBar = controlBar)}
        >
          <SeekBar
            controlBarExpanded={controlBarExpanded}
            currentTime={currentTime}
            currentUserIsOwner={currentUserIsOwner}
            duration={duration}
            isMainVideoOnSharePage={isMainVideoOnSharePage}
            fullScreen={fullScreen}
            paused={paused}
            onPause={this.props.requestVideoPlayerPause}
            onPlay={this.props.requestVideoPlayerPlay}
            containerDimensions={this.props.containerDimensions}
          />
          <div className="bottom-controls">
            <div className="left-controls">
              <PlayPauseButton
                paused={paused}
                onClick={this.handleTogglePlay}
                seeking={seeking}
              />
              <StepBackFiveButton
                onClick={this.onStepBackFive}
                isMainVideoOnSharePage={isMainVideoOnSharePage}
                seeking={seeking}
                useEmojis={useEmojis}
              />
              <StepForwardFiveButton
                onClick={this.onStepForwardFive}
                isMainVideoOnSharePage={isMainVideoOnSharePage}
                seeking={seeking}
                useEmojis={useEmojis}
              />
              <VolumeControl
                muted={muted}
                volume={volume}
                seeking={seeking}
                onButtonClick={this.handleToggleMute}
                onVolumeChange={this.props.requestVideoPlayerVolume}
              />
              {showEmojiBarInsidePlayer && (
                <TimeDisplay
                  className="next-to-volume"
                  currentTime={currentTime}
                  duration={duration}
                />
              )}
            </div>
            {!showEmojiBarInsidePlayer && (
              <TimeDisplay
                volumeControlActive={volumeControlActive}
                currentTime={currentTime}
                duration={duration}
              />
            )}
            {showEmojiBarInsidePlayer && <EmojiBar hideReaction={NERVOUS} />}
            <div className="right-controls">
              {playbackRateControl}
              {isMainVideoOnSharePage && !inTrimMode && (
                <TheaterModeButton
                  onClick={this.handleTheaterModeClick}
                  theaterMode={theaterMode}
                />
              )}
              {showPipButton && (
                <PipButton
                  onClick={togglePictureInPicture}
                  seeking={seeking}
                  useEmojis={useEmojis}
                />
              )}
              {fullScreenButton}
            </div>
          </div>
        </div>
        <div
          className={classNames('control-bar-gradient', {
            shown: !ended && controlBarExpanded,
          })}
        />
      </div>
    );
  }

  componentDidMount() {
    this.props.onRef(this);
  }

  getControlBarEl() {
    return this.controlBar;
  }

  handleTogglePlay = () => {
    if (this.props.paused) {
      this.props.requestVideoPlayerPlay();
    } else {
      this.props.requestVideoPlayerPause();
    }
  };

  onStepForwardFive = () => {
    this.props.requestVideoPlayerCurrentTime(this.props.currentTime + 5);
  };

  onStepBackFive = () => {
    this.props.requestVideoPlayerCurrentTime(this.props.currentTime - 5);
  };

  handleTheaterModeClick = () => {
    const { currentUserIsOwner, theaterMode, inTrimMode } = this.props;
    const newTheaterModeVal = inTrimMode ? false : !theaterMode;

    if (!currentUserIsOwner) {
      document.cookie = `${KEY_THEATER_MODE} = ${newTheaterModeVal};`;
    }

    this.props.updateTheaterMode(newTheaterModeVal, inTrimMode);
    analytics.track(THEATER_MODE_TOGGLED, {
      currentUserIsOwner,
      on: newTheaterModeVal,
    });
  };

  handleToggleFullscreen = () => {
    if (this.props.fullScreen) {
      this.props.requestVideoPlayerLeaveFullScreen();
    } else {
      this.props.requestVideoPlayerEnterFullScreen();
    }

    analytics.track(FULL_SCREEN_CLICK, {
      from_double_click: false,
      full_screen: this.props.fullScreen,
    });
  };

  handleToggleMute = () => {
    if (this.props.muted) {
      this.props.requestVideoPlayerUnmute();
    } else {
      this.props.requestVideoPlayerMute();
    }
  };
}
const mapStateToProps = state => {
  const {
    controlsActive,
    current_time: currentTime,
    current_user_is_owner: currentUserIsOwner,
    duration,
    full_screen: fullScreen,
    isMainVideoOnSharePage,
    muted,
    paused,
    playback_rate: playbackRate,
    seeking,
    theater_mode: theaterMode,
    volume_control_active: volumeControlActive,
    video,
    volume,
    pipSupported,
  } = state.video_player;

  const { in_trim_mode: inTrimMode } = state.trimming;

  return {
    controlsActive,
    currentTime,
    currentUserIsOwner,
    duration,
    fullScreen,
    inTrimMode,
    isMainVideoOnSharePage,
    muted,
    paused,
    playbackRate,
    seeking,
    theaterMode,
    useEmojis: video.use_emojis,
    volume,
    volumeControlActive,
    isViewerInsightsActive: selectIsActive(state),
    pipSupported,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    requestVideoPlayerCurrentTime: currentTime =>
      dispatch(requestVideoPlayerCurrentTime(currentTime)),
    updateTheaterMode: (theaterMode, inTrimMode) =>
      dispatch(updateTheaterMode(theaterMode, inTrimMode)),
    requestVideoPlayerEnterFullScreen: () =>
      dispatch(requestVideoPlayerEnterFullScreen()),
    requestVideoPlayerLeaveFullScreen: () =>
      dispatch(requestVideoPlayerLeaveFullScreen()),
    requestVideoPlayerMute: () => dispatch(requestVideoPlayerMute()),
    requestVideoPlayerPause: () => dispatch(requestVideoPlayerPause()),
    requestVideoPlayerPlay: () => dispatch(requestVideoPlayerPlay()),
    requestVideoPlayerPlaybackRate: playbackRate =>
      dispatch(requestVideoPlayerPlaybackRate(playbackRate)),
    requestVideoPlayerVolume: volume =>
      dispatch(requestVideoPlayerVolume(volume)),
    requestVideoPlayerUnmute: () => dispatch(requestVideoPlayerUnmute()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(ControlBar);
